/**
 * MenuHeader Component
 * 
 * Displays restaurant branding, logo, description, and cover photo
 * 
 * PERFORMANCE FIX (December 2025):
 * - Uses OptimizedImage with Supabase CDN transformation for WebP/resizing
 * - Cover photo uses priority loading for LCP optimization
 * - Explicit dimensions prevent CLS
 */

import React from 'react';
import { Share2 } from 'lucide-react';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import { useTranslation } from 'react-i18next';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { preloadCriticalImage } from '@/utils/imageOptimization';

interface MenuHeaderProps {
  onShareClick?: () => void;
  className?: string;
}

export function MenuHeader({ onShareClick, className = '' }: MenuHeaderProps) {
  const { customization, branch } = useMenuTheme();
  const { i18n } = useTranslation();
  const { header } = customization.components;

  // Preload cover photo for LCP optimization
  React.useEffect(() => {
    if (branch.cover_photo_url && header.showCoverPhoto && header.type !== 'simple') {
      preloadCriticalImage(branch.cover_photo_url, { width: 1280, quality: 'high' });
    }
  }, [branch.cover_photo_url, header.showCoverPhoto, header.type]);

  // Get logo dimensions based on size setting
  const getLogoDimensions = (size: string, isOverlay: boolean = false) => {
    if (isOverlay) {
      return size === 'sm' ? { width: 80, height: 80 } : 
             size === 'lg' ? { width: 112, height: 112 } : 
             { width: 96, height: 96 };
    }
    return size === 'sm' ? { width: 64, height: 64 } : 
           size === 'lg' ? { width: 96, height: 96 } : 
           { width: 80, height: 80 };
  };

  // Simple header without cover photo
  if (header.type === 'simple' || !header.showCoverPhoto || !branch.cover_photo_url) {
    const logoDimensions = getLogoDimensions(header.logoSize);
    return (
      <header className={`bg-white border-b ${className}`}>
        <div className="max-w-3xl mx-auto p-4">
          <div className="flex items-center gap-4">
            {header.showLogo && branch.restaurants?.logo_url && (
              <OptimizedImage
                src={branch.restaurants.logo_url}
                alt={branch.restaurants?.name || branch.name}
                width={logoDimensions.width}
                height={logoDimensions.height}
                containerClassName={`rounded-full shrink-0 ${
                  header.logoSize === 'sm' ? 'w-16 h-16' :
                  header.logoSize === 'lg' ? 'w-24 h-24' :
                  'w-20 h-20'
                }`}
                className="object-cover w-full h-full"
                priority={true}
                quality="high"
              />
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold">
                {branch.restaurants?.name || branch.name}
              </h1>
              {header.showDescription && branch.restaurants?.description && (
                <p className="text-gray-600 text-sm mt-1">
                  {branch.restaurants.description}
                </p>
              )}
            </div>
            {onShareClick && (
              <button 
                onClick={onShareClick}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={i18n.language === 'ar' ? 'مشاركة' : 'Share'}
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </header>
    );
  }

  // Header with cover photo
  const overlayLogoDimensions = getLogoDimensions(header.logoSize, true);
  
  return (
    <header className={`relative ${className}`}>
      {/* Cover Photo - optimized for LCP */}
      <div
        className={`relative overflow-hidden ${
          header.height === 'sm' ? 'h-32' :
          header.height === 'lg' ? 'h-64' :
          header.height === 'xl' ? 'h-80' :
          'h-48'
        }`}
        style={{
          backgroundColor: branch.cover_photo_url ? 'transparent' : '#f3f4f6'
        }}
      >
        {branch.cover_photo_url && (
          <OptimizedImage
            src={branch.cover_photo_url}
            alt="Cover"
            containerClassName="absolute inset-0 w-full h-full"
            className="object-cover w-full h-full"
            priority={true}
            layout="hero"
            quality="high"
          />
        )}
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"
          style={{ opacity: header.coverPhotoOverlay / 100 }}
        />
      </div>

      {/* Restaurant Info Card */}
      <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)]">
        <div className="bg-white rounded-2xl shadow-lg p-4 flex items-start gap-4">
          {header.showLogo && branch.restaurants?.logo_url && (
            <OptimizedImage
              src={branch.restaurants.logo_url}
              alt={branch.restaurants?.name || branch.name}
              width={overlayLogoDimensions.width}
              height={overlayLogoDimensions.height}
              containerClassName={`rounded-full border-4 border-white shrink-0 -mt-12 ${
                header.logoSize === 'sm' ? 'w-20 h-20' :
                header.logoSize === 'lg' ? 'w-28 h-28' :
                'w-24 h-24'
              }`}
              className="object-cover w-full h-full"
              priority={true}
              quality="high"
            />
          )}
          <div className="flex-1 pt-2">
            <h1 className="text-2xl font-bold">
              {branch.restaurants?.name || branch.name}
            </h1>
            {header.showDescription && branch.restaurants?.description && (
              <p className="text-gray-600 text-sm">
                {branch.restaurants.description}
              </p>
            )}
          </div>
          {onShareClick && (
            <div className="flex items-center gap-2 pt-2">
              <button 
                onClick={onShareClick}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={i18n.language === 'ar' ? 'مشاركة' : 'Share'}
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
